import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  signal, inject, DestroyRef, OnChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatOption, MatSelect } from "@angular/material/select";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenAutomationPlanUtilsService } from "../../services/loren-automation-plan-utils-service/loren-automation-plan-utils.service";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenBinaryConditionEnum } from "../../enums/loren-binary-condition.enum";
import { LorenGeofenceConditionEnum } from "../../enums/loren-geofence-condition.enum";
import { LorenGeofencingZone } from "../../../loren-geofencing/interfaces/loren-geofencing-zone.interface";
import { CommonModule } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { LorenAllConditionsEnum } from "../../enums/loren-all-conditions-enum";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";
import {
  LorenFillLevelStates,
  isFillLevelSensor,
} from "../../../asset/fill-level.utils";

interface Condition {
  key: string;
  value: string;
  metering: string;
  meteringId: number;
}

@Component({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    TranslocoPipe,
    MatButton,
  ],
  selector: "loren-automation-plan-conditions",
  templateUrl: "./loren-automation-plan-conditions.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./loren-automation-plan-conditions.component.scss"],
})
export class LorenAutomationPlanConditionsComponent implements OnInit, OnChanges {
  private translocoService = inject(TranslocoService);

  private readonly destroyRef = inject(DestroyRef);
  @Input() allAssets: LorenAsset[];
  @Input() allGeozones: LorenGeofencingZone[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() allConditions: any[];
  @Input() condition;
  @Input() mode: "new" | "adding" | "editing" | "deleting";
  @Output() conditionSubmitted = new EventEmitter();
  @ViewChild("conditionsFormTemplate") conditionsFormTemplate: NgForm;

  /// form setup
  conditionsOptions: Condition[] = [];
  geofenceOptions: { key: string; value: number }[];
  selectedAssetIdField = new FormControl(undefined, [Validators.required]);
  selectedConditionField = new FormControl<Condition>(undefined, [
    Validators.required,
  ]);
  alarmValueField = new FormControl({ value: "", disabled: true });
  warningValueField = new FormControl({ value: "", disabled: true });
  geozoneField = new FormControl({ value: "", disabled: true });
  conditionForm = new FormGroup({
    selectedAssetId: this.selectedAssetIdField,
    selectedCondition: this.selectedConditionField,
    alarmValue: this.alarmValueField,
    warningValue: this.warningValueField,
    geozone: this.geozoneField,
  });
  confirmDeleteWarningValue = signal(false);
  fillLevelOptions = LorenFillLevelStates;

  ngOnInit() {
    this.geofenceOptions = this.allGeozones.map((g) => ({
      key: g.name,
      value: g.id,
    }));

    this.assetIdSubscription();
    this.conditionSubscription();
    this.alarmValueSubscription();
    this.warningValueSubscription();
    this.formValueSubscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.condition || changes.mode) {
      if (this.mode === "editing" || this.mode === "deleting") {
        this.updateForm();
      } else this.conditionForm.reset();

      if (this.mode === "deleting") {
        this.conditionForm.disable({ emitEvent: false });
      } else this.conditionForm.enable({ emitEvent: false });
    }
  }

  getConditionFields(rule) {
    if (this.isConditionBinary(rule)) {
      [this.geozoneField, this.alarmValueField, this.warningValueField].forEach(
        (f) => {
          f.clearValidators();
          f.updateValueAndValidity();
        }
      );
      return;
    }
    if (this.isConditionGeofence(rule)) {
      this.geozoneField.setValidators(Validators.required);
      this.alarmValueField.clearValidators();
      this.warningValueField.clearValidators();
      [this.geozoneField, this.alarmValueField, this.warningValueField].forEach(
        (f) => f.updateValueAndValidity()
      );
      return;
    }
    this.geozoneField.clearValidators();
    this.alarmValueField.setValidators([this.validateAlarmOrWarningRequired()]);
    this.warningValueField.addValidators([this.validateAlarmOrWarningRequired()]);
    [this.geozoneField, this.alarmValueField, this.warningValueField].forEach((f) =>
      f.updateValueAndValidity()
    );
  }

  getWarningValueErrorMsg() {
    if (this.warningValueField.hasError("warningAndAlarmValuesAreDifferent")) {
      return this.translocoService.translate(
        "automation_plans_form.warning_value_must_differ"
      );
    }

    return this.numericConditionLimit(
      this.selectedConditionField.value?.value
    ) === "LOWER"
      ? this.translocoService.translate(
          "automation_plans_form.warning_value_cannot_be_lower"
        )
      : this.translocoService.translate(
          "automation_plans_form.warning_value_cannot_be_higher"
        );
  }

  isConditionBinary(condition) {
    return Object.values(LorenBinaryConditionEnum).includes(condition);
  }

  isConditionGeofence(condition) {
    return Object.values(LorenGeofenceConditionEnum).includes(condition);
  }

  isConditionFillLevel(condition?: Condition | string | null) {
    const selectedCondition = typeof condition === "string" ? condition : condition?.value;
    const meteringType = typeof condition === "string"
      ? this.getSelectedConditionMeteringType()
      : this.getMeteringType(condition?.meteringId);

    return (
      selectedCondition === LorenAllConditionsEnum.FILL_LEVEL_EQUALS &&
      isFillLevelSensor(meteringType)
    );
  }

  numericConditionLimit(condition: string) {
    return condition.includes("HIGHER")
      ? "UPPER"
      : condition.includes("LOWER")
      ? "LOWER"
      : null;
  }

  submitForm() {
    const { selectedCondition, geozone, alarmValue, warningValue } =
      this.conditionForm.value;
    const { value: rule, meteringId } = selectedCondition;
    const newRule = {
      meteringId: meteringId,
      condition: rule,
      areaId: geozone,
      value: this.isConditionBinary(rule)
        ? LorenAutomationPlanUtilsService.binaryConditionsValues.get(
            rule as LorenAllConditionsEnum
          )
        : alarmValue,
      warningValue: warningValue,
      ruleType: this.isConditionBinary(rule)
        ? "binary"
        : this.isConditionGeofence(rule)
        ? "geofence"
        : "numeric",
      mode: this.mode,
      id: this.condition?.id || false,
    };
    this.closeForm(newRule);
  }

  closeForm(rule = null) {
    this.conditionSubmitted.emit(rule);
    this.conditionForm.reset();
    this.conditionsFormTemplate.resetForm();
  }

  compareCondition(c1, c2) {
    return c1.value === c2.value && c1.meteringId === c2.meteringId;
  }

  compareAsset(a1, a2) {
    return a1.id === a2.id;
  }

  compareFillLevelOption(optionA: number, optionB: number) {
    return Number(optionA) === Number(optionB);
  }

  private assetIdSubscription() {
    return this.selectedAssetIdField.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((v) => {
      this.getConditionsFromAsset(Number(v?.id));
      [
        this.selectedConditionField,
        this.alarmValueField,
        this.warningValueField,
        this.geozoneField,
      ].forEach((f) => {
        f.reset();
        f.updateValueAndValidity();
      });
      this.selectedConditionField.enable();
    });
  }

  private conditionSubscription() {
    return this.selectedConditionField.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((v) => {
      this.getConditionFields(v?.value);
      [this.alarmValueField, this.warningValueField, this.geozoneField].forEach(
        (f) => f.enable()
      );
      if (v?.value && this.alarmValueField.value) {
        this.getWarningValueValidation(v?.value, this.alarmValueField.value);
      }
    });
  }

  private alarmValueSubscription() {
    return this.alarmValueField.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      if(this.selectedConditionField.value) {
        this.getWarningValueValidation(
          this.selectedConditionField.value.value,
          value
        );
      }
      this.warningValueField.updateValueAndValidity({ emitEvent: false });
    });
  }

  private warningValueSubscription() {
    return this.warningValueField.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.alarmValueField.updateValueAndValidity({ emitEvent: false });
    });
  }

  private formValueSubscription() {
    return this.conditionForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((v) => {
      if (
        this.mode === "adding" &&
        this.allConditions.some((c) => {
          const condition = v.selectedCondition;
          return (
            c.condition === condition?.value &&
            c.meteringId === condition?.meteringId &&
            (c.value === v.alarmValue || c.areaId === v.geozone)
          );
        })
      ) {
        this.conditionForm.setErrors({ conditionExists: true });
      }
    });
  }

  private getConditionsFromAsset(id: number) {
    const assetMeterings = this.getAssetFromId(id)?.metering;
    this.conditionsOptions = assetMeterings
      ?.map((m) => {
        const rule =
          LorenAutomationPlanUtilsService.conditionsBasedOnMeteringTypes.get(
            m.type
          );
        return rule?.map((r) => ({
          ...r,
          metering: m.description,
          meteringId: m.id,
        }));
      })
      .flat()
      .filter((m) => m);
  }

  private getAssetFromId(id: number) {
    return this.allAssets.find((a) => a.id === id);
  }

  private updateForm() {
    const asset = this.condition.asset;
    const metering = asset.metering.find(
      (m) => m.id === this.condition.meteringId
    );
    this.getConditionsFromAsset(asset.id);

    const condition = this.conditionsOptions?.find(
      (c) =>
        c.meteringId === this.condition.meteringId &&
        (c.value === this.condition.condition ||
          c.value ===
            LorenAutomationPlanUtilsService.binaryValuesConditions.get(
              metering?.type
            )?.[this.condition.value ? 1 : 0])
    );

    this.conditionForm.setValue({
      selectedAssetId: asset || null,
      selectedCondition: condition || null,
      alarmValue: this.condition.value ?? "",
      warningValue: this.condition.warningValue ?? "",
      geozone: this.condition.areaId || "",
    });

    this.getWarningValueValidation(condition.value, this.condition.value);
  }

  private getWarningValueValidation(rule: string, value) {
    if (!rule || this.isConditionBinary(rule) || this.isConditionGeofence(rule)) {
      return;
    }
    const condition = this.numericConditionLimit(rule);
    const validators: ValidatorFn[] = [
      this.validateWarningAndAlarmValuesAreDifferent(),
      this.validateAlarmOrWarningRequired(),
    ];

    if (value && !this.isConditionFillLevel(rule)) {
      if(condition === "LOWER") {
        validators.push(Validators.min(Number(value)));
      } else {
        validators.push(Validators.max(Number(value)));
      }
    }

    this.warningValueField.setValidators(validators);
    this.warningValueField.updateValueAndValidity();
  }

  private validateWarningAndAlarmValuesAreDifferent() {
    return (control: AbstractControl) => {
      const value = Number(control.value);
      const alarmValue = this.alarmValueField.value;
      if (control.value === null || control.value === "" || alarmValue === null || alarmValue === "") return null;
      return Number(alarmValue) === value
        ? { warningAndAlarmValuesAreDifferent: true }
        : null;
    };
  }

  private validateAlarmOrWarningRequired(): ValidatorFn {
    return () => {
      const alarmValue = this.alarmValueField.value;
      const warningValue = this.warningValueField.value;

      if (alarmValue === null && warningValue === null) {
        return { alarmOrWarningRequired: true };
      }
      return null;
    };
  }

  private getSelectedConditionMeteringType() {
    return this.getMeteringType(this.selectedConditionField.value?.meteringId);
  }

  private getMeteringType(meteringId: number | undefined) {
    const asset = this.selectedAssetIdField.value ?? this.condition?.asset;
    return asset?.metering.find((metering) => metering.id === meteringId)?.type;
  }
}
